import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { translate } from 'react-polyglot';
import List from '../List';
import Dropdown from '../Dropdown';
import Link from '../Link';
import SvgIcon from '../SvgIcon';
import {
    xShareUrl,
    facebookShareUrl,
    emailShareUrl,
    whatsappWebUrl,
    whatsappMobileUrl,
    linkedInShareUrl,
} from '../../lib/social';

import './ShareMenu.scss';

const ShareMenu = ({
    t,
    title,
    thumbUrl,
    contentUrl,
    href,
    align,
    onClick,
    videoShare,
    isIllustration,
    readingList,
    landingPageBook,
    hideTitle,
}) => {
    const [isMobile, setIsMobile] = useState(false);
    const [isMobileScreen, setIsMobileScreen] = useState(
        window.innerWidth <= 768
    );
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        const checkIsMobileDevice = () => {
            const isMobileDevice =
                /Android|webOS|iPhone|iPad|iPod|BlackBerry|Windows Phone/i.test(
                    navigator.userAgent
                );
            setIsMobile(isMobileDevice);
        };
        checkIsMobileDevice();

        const handleResize = () => {
            setIsMobileScreen(window.innerWidth <= 768);
        };
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const handleNativeShare = (title, url) => {
        if (navigator.share) {
            navigator
                .share({
                    title,
                    //   text: summary,
                    url,
                })
                .catch(() => {});
        } else {
            console.log('Native sharing is not supported on this device.');
        }
    };

    const copyLinkToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(href);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000); // Reset after 2 seconds
        } catch (err) {
            alert('Failed to copy link. Please copy manually.');
        }
    };

    const encodedUrl = encodeURIComponent(href);

    return (
        <Dropdown
            align={align}
            toggleEl={
                <Link
                    favouriteShare={true}
                    normal
                    readingList={readingList ? true : false}
                    dataGTM={
                        isIllustration ? 'illustration-share' : 'book-share'
                    }
                >
                    {!videoShare && !landingPageBook && (
                        <SvgIcon name="Share" size="share-book-video" />
                    )}
                    {landingPageBook && (
                        <div>
                            <SvgIcon
                                name="Share"
                                size="book-detail-action-controls"
                            />
                        </div>
                    )}
                    {hideTitle ? null : <span>{t('global.share', 1)}</span>}
                </Link>
            }
        >
            <List nowrap socialShareIcons>
                {!(isMobile && isMobileScreen) && <Link
                    fullWidthShare
                    href={xShareUrl(href)}
                    shouldOpenNewPage={true}
                    onClick={onClick}
                    dataGTM={
                        isIllustration
                            ? 'illustration-twitter-share'
                            : 'book-twitter-share'
                    }
                >
                    <SvgIcon
                        name="X"
                        size="m"
                        pushCenter
                        dataGTM={
                            isIllustration
                                ? 'illustration-twitter-share'
                                : 'book-twitter-share'
                        }
                    />{' '}
                    {t('global.twitter')}
                </Link>}
                {!(isMobile && isMobileScreen) && <Link
                    fullWidthShare
                    href={facebookShareUrl(href)}
                    shouldOpenNewPage={true}
                    onClick={onClick}
                    dataGTM={
                        isIllustration
                            ? 'illustration-facebook-share'
                            : 'book-facebook-share'
                    }
                >
                    <SvgIcon
                        name="facebook"
                        size="m"
                        pushCenter
                        dataGTM={
                            isIllustration
                                ? 'illustration-facebook-share'
                                : 'book-facebook-share'
                        }
                    />{' '}
                    {t('global.facebook')}
                </Link>}
                <Link
                    fullWidthShare
                    href={emailShareUrl(href, title)}
                    shouldOpenNewPage={true}
                    onClick={onClick}
                    dataGTM={
                        isIllustration
                            ? 'illustration-email-share'
                            : 'book-email-share'
                    }
                >
                    <SvgIcon
                        name="mail"
                        size="m"
                        pushCenter
                        dataGTM={
                            isIllustration
                                ? 'illustration-email-share'
                                : 'book-email-share'
                        }
                    />{' '}
                    {t('global.mailTo')}
                </Link>
                <Link
                    fullWidthShare
                    href={
                        isMobile && isMobileScreen
                            ? whatsappMobileUrl(encodedUrl)
                            : whatsappWebUrl(encodedUrl)
                    }
                    shouldOpenNewPage={true}
                    onClick={onClick}
                    dataGTM={
                        isIllustration
                            ? 'illustration-whats-app-share'
                            : 'book-whats-app-share'
                    }
                >
                    <SvgIcon
                        name="whatsapp"
                        size="m"
                        pushCenter
                        dataGTM={
                            isIllustration
                                ? 'illustration-whats-app-share'
                                : 'book-whats-app-share'
                        }
                    />{' '}
                    {isMobile && isMobileScreen
                        ? t('share.whatsapp')
                        : t('share.whatsappWeb')}
                </Link>
                <Link
                    fullWidthShare
                    href={linkedInShareUrl(href)}
                    shouldOpenNewPage={true}
                    onClick={onClick}
                >
                    <SvgIcon
                        name="linkedin"
                        size="m"
                        pushCenter
                        dataGTM={
                            isIllustration
                                ? 'illustration-email-share'
                                : 'book-email-share'
                        }
                    />{' '}
                    {t('share.linkedin')}
                </Link>
                {isMobile && isMobileScreen && navigator.share && (
                    <Link
                        fullWidthShare
                        shouldOpenNewPage={false}
                        onClick={() => handleNativeShare(title, href)}
                    >
                        <SvgIcon
                            name="chain"
                            size="m"
                            pushCenter
                            dataGTM={
                                isIllustration
                                    ? 'illustration-email-share'
                                    : 'book-email-share'
                            }
                        />{' '}
                        {t('share.copyLink')}
                    </Link>
                )}
                {window.matchMedia("(min-width: 1024px)").matches &&(
                    <Link
                        fullWidthShare
                        shouldOpenNewPage={false}
                        onClick={copyLinkToClipboard}
                    >
                        <SvgIcon
                            name="chain"
                            size="m"
                            pushCenter
                            dataGTM={
                                isIllustration
                                    ? 'illustration-email-share'
                                    : 'book-email-share'
                            }
                        />{' '}
                        {copied ? (
                            <span style={{ color: 'green' }}>
                                {t('share.copied')}
                            </span>
                        ) : (
                            t('share.copyLink')
                        )}
                    </Link>
                )}
            </List>
        </Dropdown>
    );
};

ShareMenu.propTypes = {
    title: PropTypes.string,
    href: PropTypes.string.isRequired,
    align: PropTypes.shape(Dropdown.propTypes.align),
    onClick: PropTypes.func,
    videoShare: PropTypes.bool,
    isIllustration: PropTypes.bool,
    readingList: PropTypes.bool,
    landingPageBook: PropTypes.bool,
    hideTitle: PropTypes.bool,
};

export default translate()(ShareMenu);
